/*
 * Purpose: Mission knowledge server sync + pgvector retrieval
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import type { MissionKnowledgeDoc, MissionKnowledgeSessionData } from './types';

export interface MissionKnowledgeSyncResult {
  ok: boolean;
  synced: number;
  embeddedChunks?: number;
  message?: string;
}

export interface MissionKnowledgeRetrieveResult {
  ok: boolean;
  contextBlock: string;
  chunkCount: number;
  message?: string;
}

export interface RetrievedChunk {
  content: string;
  documentTitle: string;
  documentKind: string;
  score: number;
}

function missionBase(missionSessionId: string) {
  return `/api/missions/${encodeURIComponent(missionSessionId)}`;
}

/** Push full session (docs + structured answers) to Render Postgres + embed. */
export async function syncMissionSessionToServer(
  missionSessionId: string,
  data: MissionKnowledgeSessionData,
  rawOutcome?: string
): Promise<MissionKnowledgeSyncResult> {
  if (!missionSessionId) return { ok: true, synced: 0 };

  try {
    const res = await fetch(`${missionBase(missionSessionId)}/documents`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        rawOutcome: rawOutcome ?? null,
        structuredAnswers: data.structuredAnswers,
        documents: data.docs,
      }),
    });

    if (res.status === 503) {
      return { ok: true, synced: 0, message: 'Server RAG not configured — local library only' };
    }

    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      return { ok: false, synced: 0, message: body.error ?? `Sync failed (${res.status})` };
    }

    const body = (await res.json()) as { synced?: number; embeddedChunks?: number };
    return {
      ok: true,
      synced: body.synced ?? data.docs.length,
      embeddedChunks: body.embeddedChunks,
    };
  } catch {
    return { ok: true, synced: 0, message: 'Offline — mission library kept on device' };
  }
}

/** @deprecated use syncMissionSessionToServer */
export async function syncMissionDocsToServer(
  missionSessionId: string,
  docs: MissionKnowledgeDoc[]
): Promise<MissionKnowledgeSyncResult> {
  return syncMissionSessionToServer(missionSessionId, {
    docs,
    structuredAnswers: {},
  });
}

export async function deleteMissionDocOnServer(
  missionSessionId: string,
  docId: string
): Promise<{ ok: boolean; message?: string }> {
  if (!missionSessionId || !docId) return { ok: true };
  try {
    const res = await fetch(`${missionBase(missionSessionId)}/documents/${encodeURIComponent(docId)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (res.status === 503) return { ok: true, message: 'RAG disabled' };
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      return { ok: false, message: body.error ?? `Delete failed (${res.status})` };
    }
    return { ok: true };
  } catch {
    return { ok: true, message: 'Offline' };
  }
}

/** pgvector retrieval — top chunks for planner/interpret query. */
export async function retrieveMissionContext(
  missionSessionId: string,
  query: string,
  topK = 8
): Promise<MissionKnowledgeRetrieveResult> {
  if (!missionSessionId || !query.trim()) {
    return { ok: true, contextBlock: '', chunkCount: 0 };
  }

  try {
    const res = await fetch(`${missionBase(missionSessionId)}/retrieve`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: query.trim(), topK }),
    });

    if (res.status === 503) {
      return { ok: true, contextBlock: '', chunkCount: 0, message: 'RAG not configured' };
    }

    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      return { ok: false, contextBlock: '', chunkCount: 0, message: body.error };
    }

    const body = (await res.json()) as { chunks?: RetrievedChunk[] };
    const chunks = body.chunks ?? [];
    if (chunks.length === 0) {
      return { ok: true, contextBlock: '', chunkCount: 0 };
    }

    const contextBlock = chunks
      .map(
        (c, i) =>
          `[${i + 1}] (${c.documentKind}: ${c.documentTitle}, score ${(c.score * 100).toFixed(0)}%)\n${c.content}`
      )
      .join('\n\n')
      .slice(0, 8000);

    return { ok: true, contextBlock, chunkCount: chunks.length };
  } catch {
    return { ok: true, contextBlock: '', chunkCount: 0, message: 'Offline' };
  }
}

export async function fetchMissionRagStatus(missionSessionId: string): Promise<boolean> {
  try {
    const res = await fetch(`${missionBase(missionSessionId)}/documents`, {
      credentials: 'include',
    });
    return res.ok && res.status !== 503;
  } catch {
    return false;
  }
}
